import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { userService } from '@/services/storageService';
import { useI18n } from '@/contexts/I18nContext';

export default function ConsentGate() {
  const { t } = useI18n();
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [acceptTermsAndConditions, setAcceptTermsAndConditions] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { token, setSessionFromToken } = useAuth();

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!acceptPrivacy || !acceptTermsAndConditions) {
      toast.error(t('auth_accept_both_policies', 'Please accept both policies to continue'));
      return;
    }
    if (!token) {
      toast.error(t('auth_session_expired_signin_again', 'Session expired. Please sign in again.'));
      navigate('/login', { replace: true });
      return;
    }

    setIsSubmitting(true);
    try {
      await userService.acceptRequiredConsents({ source: 'consent_gate' });
      const refreshed = await setSessionFromToken(token);
      if (!refreshed) {
        toast.error(t('auth_session_refresh_failed', 'Session refresh failed. Please sign in again.'));
        navigate('/login', { replace: true });
        return;
      }
      toast.success(t('auth_consent_saved', 'Thanks. Consent recorded.'));
      if (refreshed.isSuperAdmin) {
        navigate('/super-admin', { replace: true });
      } else {
        navigate('/select-club', { replace: true });
      }
    } catch (err: any) {
      toast.error(err?.message || t('auth_consent_save_failed', 'Could not save your consent'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/10 via-background to-accent/10 p-4">
      <Card className="w-full max-w-5xl border-border/50 shadow-xl">
        <CardHeader className="space-y-3 pb-5">
          <CardTitle className="text-5xl font-display">{t('consent_before_continue', 'Before you continue')}</CardTitle>
          <CardDescription className="text-xl">
            {t('consent_review_accept_both', 'Please review and accept both policies to access the app.')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-8">
                <Label className="flex items-start gap-4 text-xl font-medium leading-tight">
              <Checkbox
                id="privacy-consent"
                checked={acceptPrivacy}
                onCheckedChange={(value) => setAcceptPrivacy(!!value)}
                className="mt-0.5 h-7 w-7 shrink-0"
              />
                <span className="text-muted-foreground">
                {t('first_login_agree_privacy', 'I agree to the')}{' '}
                <Link to="/privacy-policy" className="text-primary hover:underline">
                  {t('privacy_title', 'Privacy Policy')}
                </Link>
                .
              </span>
            </Label>
            <Label className="flex items-start gap-4 text-xl font-medium leading-tight">
              <Checkbox
                id="data-consent"
                checked={acceptTermsAndConditions}
                onCheckedChange={(value) => setAcceptTermsAndConditions(!!value)}
                className="mt-0.5 h-7 w-7 shrink-0"
              />
              <span className="text-muted-foreground">
                {t('first_login_agree_terms', 'I agree to the')}{' '}
                <Link to="/terms-and-conditions" className="text-primary hover:underline">
                  {t('terms_title', 'Terms and Conditions')}
                </Link>
                .
              </span>
            </Label>

            <Button
              type="submit"
              className="w-full h-16 text-xl"
              disabled={isSubmitting || !acceptPrivacy || !acceptTermsAndConditions}
            >
              {isSubmitting ? t('consent_saving', 'Saving...') : t('consent_accept_continue', 'Accept and continue')}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
